// ============================================================================
// BOOK · the stand-in photo reads (ORD-BOOK-1b) — no SQL, the tables' own RLS
// ============================================================================
//   other   every live library model photo of the card's codes (files_links + files_assets,
//           has_files_access): a colour with no angle (NOT _s), options, no colour (ORD-BOOK-1e)
//   sample  sample-sheet colourways with a photo (files_colourways, has_files_access);
//           the picture is files-bank extracted/… (Orders users may read it, D8)
//   dev     the development card images (developments, readable by every signed-in user;
//           public item-images URLs; data.primaryImageIndex first, as getDevPrimaryImage)
// Each answer is keyed like the side read: by the code a card is filed under.
// ============================================================================
import { supabase } from '../../../supabase';
import { indexDevRows, indexLibraryRows } from '../model/bookSourceIndex';
import { sheetIndex } from '../model/bookRnc';

const PAGE = 1000;
const CHUNK = 300;     // codes per request: ~10 chars each, far under the URL ceiling (Conv #167)
const fail = (error) => { const e = new Error(error.message || String(error)); e.code = error.code; throw e; };
const chunks = (a, n) => Array.from({ length: Math.ceil(a.length / n) }, (_, i) => a.slice(i * n, i * n + n));

async function paged(build) {
  const rows = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await build().range(from, from + PAGE - 1);
    if (error) fail(error);
    rows.push(...(data || []));
    if (!data || data.length < PAGE) break;
  }
  return rows;
}

/** { other, option, nocol } (bookSourceIndex.indexLibraryRows) — every live library photo of the codes. */
export async function fetchOtherPhotos(keys) {
  const rows = [];
  for (const part of chunks(keys, CHUNK)) {
    rows.push(...await paged(() => supabase.from('files_links')
      .select('entity_key,files_assets!inner(id,storage_path,summary,photo,uploaded_at,decision,kind,archived,original_name,display_name)')
      .in('entity_key', part).is('detached_at', null)
      .eq('files_assets.kind', 'model_photo').eq('files_assets.archived', false)
      .order('id', { ascending: true })));
  }
  return indexLibraryRows(rows);
}

const colourOf = (code) => { const m = String(code || '').match(/-\s*0*(\d{1,3})\b/); return m ? Number(m[1]) : null; };

/** { DEV: [{ n, p, frame, lens, ordered, row }] } in sheet order — one row per colourway number (newest sheet). */
export async function fetchSampleColourways(devs) {
  const out = {};
  for (const part of chunks(devs, CHUNK)) {
    const rows = await paged(() => supabase.from('files_colourways')
      .select('development_code,item_code,frame_colour,lens_text,photo_path,ordered_as,row_index,created_at')
      .in('development_code', part).not('photo_path', 'is', null)
      .order('created_at', { ascending: false }).order('row_index', { ascending: true }));
    for (const r of rows) {
      const n = colourOf(r.item_code);
      if (!n) continue;
      const k = String(r.development_code).toUpperCase().trim();
      const list = (out[k] = out[k] || []);
      if (list.some((x) => x.n === n)) continue;            // newest sheet first: keep its row
      list.push({ n, p: r.photo_path, frame: r.frame_colour || null, lens: r.lens_text || null, ordered: !!(r.ordered_as && String(r.ordered_as).trim()), row: r.row_index ?? 999 });
    }
  }
  for (const k of Object.keys(out)) out[k].sort((a, b) => a.row - b.row || a.n - b.n);
  return out;
}

/** { dev: { DEV: primary url }, devAll: { DEV: [urls] } } — the development card's images (bookSourceIndex). */
export async function fetchDevPhotos(devs) {
  const rows = [];
  for (const part of chunks(devs, CHUNK)) {
    rows.push(...await paged(() => supabase.from('developments')
      .select('id,dev_number:data->>dev_number,images:data->images,primaryImageIndex:data->primaryImageIndex')
      .in('data->>dev_number', part).is('deleted_at', null).order('id', { ascending: true })));
  }
  return indexDevRows(rows);
}

// ORD-BOOK-1f · the RNC lines' PI sheets in the order corpus (orders_corpus_*, has_orders_access — the Book's
// readers) and the decision emails' new-colour pictures (decision_emails, every signed-in user).
const PI_CHUNK = 40;     // pi prefixes per request: "file_name.like.26080037*" ≈ 30 chars each

/** sheetIndex(files, rows) for every file whose name starts with one of `pis`. */
export async function fetchPiSheets(pis) {
  const files = [];
  for (const part of chunks(pis, PI_CHUNK)) {
    files.push(...await paged(() => supabase.from('orders_corpus_files').select('id,file_name')
      .or(part.map((p) => `file_name.like.${p}*`).join(',')).order('id', { ascending: true })));
  }
  const rows = [];
  for (const part of chunks(files.map((f) => f.id), CHUNK)) {
    rows.push(...await paged(() => supabase.from('orders_corpus_rows')
      .select('file_id,item,is_option,remark,color_name,frame_color')
      .in('file_id', part).order('id', { ascending: true })));
  }
  return sheetIndex(files, rows);
}

/** { ITEM: [{ desc, sort, image_url, qty, ref }] } — the decision emails' new colours of these models. */
export async function fetchNewColourEmails(items) {
  const out = {};
  for (const part of chunks(items, CHUNK)) {
    const rows = await paged(() => supabase.from('decision_emails').select('model,new_colors').in('model', part).order('model', { ascending: true }));
    for (const r of rows) if (Array.isArray(r.new_colors) && r.new_colors.length) out[String(r.model).toUpperCase()] = r.new_colors;
  }
  return out;
}
